import { filter } from 'lodash';
import {
  failIfValidOrNonExhaustive,
  ILoadable,
  LoadableStatus,
  CountryMetadatumLevel as Level,
} from '../../Utils';
import {
  GetNumericValue,
  SubSectionGetter,
  WithSection,
  WithValue,
} from './internalUtils';
import { extractFromStringGroupId } from '../../graphQL/Utils';

type OutputDatum<T> = WithSection<WithValue<T>>;

const assignAndFilterSection = <T>(
    dataStatus: ILoadable<Array<WithValue<T>>>,
    getSectionStatus: ILoadable<GetNumericValue<T>>,
    getSubSectionStatus: ILoadable<SubSectionGetter<T>>,
    deselectedCategories: number[],
    getColor: (section: number) => string,
    subregionToRegionMap,
    level
  ) => {

  let result: ILoadable<Array<OutputDatum<T>>>;
  if (dataStatus.status === LoadableStatus.Initial ||
      getSectionStatus.status === LoadableStatus.Initial ||
      getSubSectionStatus.status === LoadableStatus.Initial) {
    result = {status: LoadableStatus.Initial};
  } else if (dataStatus.status === LoadableStatus.Loading ||
              getSectionStatus.status === LoadableStatus.Loading ||
              getSubSectionStatus.status === LoadableStatus.Loading) {
    result = {status: LoadableStatus.Initial};
  } else if (dataStatus.status === LoadableStatus.NotPresent ||
              getSectionStatus.status === LoadableStatus.NotPresent ||
              getSubSectionStatus.status === LoadableStatus.NotPresent) {

    result = {status: LoadableStatus.Initial};
  } else if (dataStatus.status === LoadableStatus.Present) {
    const {data} = dataStatus;
    const {data: getSection} = getSectionStatus;
    const {data: getSubSection} = getSubSectionStatus;

    // Remove all negative trade values (possible with net export/import):
    const filteredByValue = data.filter(({value}) => value > 0)
      // Remove locationId = 250 -- Undeclared
      .filter(({partner_id}) => partner_id != 250);


    const deselectedCategoriesSet = new Set(deselectedCategories);

    // Assign section and subsection to each datum and exclude sections that
    // the user has deselected:
    const withSection: Array<OutputDatum<T>> = [];
    for (const datum of filteredByValue) {
      const section = getSection(datum);

      
      let useCategoryForDeselectionTest;
      let regionIdForOrderingByRegion;
      // In the case of using assignSection to assign countries to regions/subregions,
      // need to use a mapped value for deselection test -- i.e., the subregion (section)
      // mapped to its corresponding top-level region (group-##)
      if(level === Level.subregion && subregionToRegionMap !== undefined) {
        useCategoryForDeselectionTest = subregionToRegionMap.get(section);
      } else {
        // Otherwise, in other cases (i.e., CPY_C), just use the assigned section
        useCategoryForDeselectionTest = section;
      }

      if (section !== undefined && deselectedCategoriesSet.has(useCategoryForDeselectionTest) === false) {
        const {
          subSection, shortLabel, longLabel, dropdownLabel, dropdownSearchString, dropdownValue,
        } = getSubSection(datum);
        let colorAssignment;

        // If dealing with locations, determine region ID for ordering ribbons in stack chart
        if(subregionToRegionMap !== undefined) {
          regionIdForOrderingByRegion = extractFromStringGroupId(section);
          if(level === Level.subregion) {
            const mappedSubregionToRegion = subregionToRegionMap.get(section);
            if(mappedSubregionToRegion) {
              regionIdForOrderingByRegion = extractFromStringGroupId(mappedSubregionToRegion);
              colorAssignment = getColor(mappedSubregionToRegion);
            } else {
              colorAssignment = "gray";
            }
          } else {
            colorAssignment = getColor(section);
          }
        } else {
          // Otherwise, for products, just get assigned color from product section
          colorAssignment = getColor(section);
        }

        const datumWithSection: OutputDatum<T> = Object.assign({}, datum, {
          section, subSection, regionIdForOrderingByRegion,
          color: colorAssignment,
          shortLabel, longLabel,
          dropdownLabel, dropdownSearchString, dropdownValue,
        });
        withSection.push(datumWithSection);
      }
    }


    
    result = {
      status: LoadableStatus.Present,
      data: withSection,
    };
  } else {
    failIfValidOrNonExhaustive(dataStatus, 'Invalid loadable status');
    // The following lines will never be executed:
    result = {status: LoadableStatus.NotPresent};
  }
  return result;
};

export default assignAndFilterSection;
